import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface UploadTask {
  id: string;
  name: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error' | 'cancelled';
  size?: number;
  uploaded?: number;
  speed?: number;
  eta?: number;
  error?: string;
}

export interface UploadOverlayState {
  visible: boolean;
  title: string;
  subtitle?: string;
  tasks: UploadTask[];
  totalProgress: number;
  canCancel: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UploadProgressOverlayService {
  private initialState: UploadOverlayState = {
    visible: false,
    title: 'Upload en cours',
    subtitle: undefined,
    tasks: [],
    totalProgress: 0,
    canCancel: true
  };

  private state$ = new BehaviorSubject<UploadOverlayState>(this.initialState);

  /**
   * Get the current overlay state as an observable
   */
  getState(): Observable<UploadOverlayState> {
    return this.state$.asObservable();
  }

  /**
   * Show the upload overlay with initial tasks
   */
  show(config: {
    title?: string;
    subtitle?: string;
    tasks: Array<{ id: string; name: string; size?: number }>;
    canCancel?: boolean;
  }): void {
    const tasks: UploadTask[] = config.tasks.map(task => ({
      id: task.id,
      name: task.name,
      progress: 0,
      status: 'pending',
      size: task.size,
      uploaded: 0
    }));

    this.state$.next({
      visible: true,
      title: config.title || 'Upload en cours',
      subtitle: config.subtitle,
      tasks,
      totalProgress: 0,
      canCancel: config.canCancel !== undefined ? config.canCancel : true
    });
  }

  /**
   * Update progress for a specific task
   */
  updateTaskProgress(taskId: string, progress: {
    progress?: number;
    uploaded?: number;
    speed?: number;
    eta?: number;
  }): void {
    const currentState = this.state$.getValue();
    const tasks = currentState.tasks.map(task => {
      if (task.id === taskId) {
        return {
          ...task,
          progress: progress.progress !== undefined ? progress.progress : task.progress,
          uploaded: progress.uploaded !== undefined ? progress.uploaded : task.uploaded,
          speed: progress.speed,
          eta: progress.eta
        };
      }
      return task;
    });

    const totalProgress = this.calculateTotalProgress(tasks);

    this.state$.next({
      ...currentState,
      tasks,
      totalProgress
    });
  }

  /**
   * Update status for a specific task
   */
  updateTaskStatus(taskId: string, status: UploadTask['status'], error?: string): void {
    const currentState = this.state$.getValue();
    const tasks = currentState.tasks.map(task => {
      if (task.id === taskId) {
        return {
          ...task,
          status,
          error,
          progress: status === 'completed' ? 100 : task.progress
        };
      }
      return task;
    });

    const totalProgress = this.calculateTotalProgress(tasks);

    this.state$.next({
      ...currentState,
      tasks,
      totalProgress
    });
  }

  /**
   * Hide the overlay
   */
  hide(): void {
    this.state$.next(this.initialState);
  }

  /**
   * Cancel all uploads
   */
  cancelAll(): void {
    const currentState = this.state$.getValue();
    const tasks = currentState.tasks.map(task => ({
      ...task,
      status: task.status === 'uploading' || task.status === 'pending'
        ? 'cancelled' as const
        : task.status
    }));

    this.state$.next({
      ...currentState,
      tasks
    });
  }

  /**
   * Check if all uploads are complete
   */
  isComplete(): boolean {
    const tasks = this.state$.getValue().tasks;
    return tasks.length > 0 && tasks.every(task =>
      task.status === 'completed' || task.status === 'error' || task.status === 'cancelled'
    );
  }

  /**
   * Get number of completed tasks
   */
  getCompletedCount(): number {
    return this.state$.getValue().tasks.filter(task => task.status === 'completed').length;
  }

  /**
   * Get number of failed tasks
   */
  getFailedCount(): number {
    return this.state$.getValue().tasks.filter(task => task.status === 'error').length;
  }

  /**
   * Calculate total progress across all tasks
   */
  private calculateTotalProgress(tasks: UploadTask[]): number {
    if (tasks.length === 0) return 0;

    const totalProgress = tasks.reduce((sum, task) => sum + task.progress, 0);
    return Math.round(totalProgress / tasks.length);
  }

  /**
   * Format file size for display
   */
  formatSize(bytes: number): string {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k**i).toFixed(1))} ${sizes[i]}`;
  }

  /**
   * Format speed for display
   */
  formatSpeed(bytesPerSecond: number): string {
    if (!bytesPerSecond || bytesPerSecond === 0) return '0 KB/s';
    const k = 1024;
    const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
    const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
    return `${parseFloat((bytesPerSecond / k**i).toFixed(1))} ${sizes[i]}`;
  }

  /**
   * Format ETA for display
   */
  formatETA(seconds: number): string {
    if (!seconds || seconds <= 0 || !isFinite(seconds)) return 'Calcul...';

    if (seconds < 60) {
      return `${Math.ceil(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const secs = Math.ceil(seconds % 60);
      return `${minutes}m ${secs}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }
}
